import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiPackage,
  FiPlus,
  FiSave,
  FiSend,
  FiTrash2,
  FiTruck,
  FiX,
} from "react-icons/fi";
import {
  addStockRequestItem,
  deleteStockRequest,
  deleteStockRequestItem,
  getCategories,
  getLastStockItemForEachVariety,
  getProducts,
  getStockRequestById,
  sendStockRequest,
  updateStockRequest,
  updateStockRequestItem,
} from "../api";
import StockProductPickerPanel from "../components/StockProductPickerPanel";
import { normalizePaginatedResponse } from "../utils/pagination";
import { resolvePriceClientType } from "../utils/clientType";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import InlineLoading from "../components/InlineLoading";

const STATIC_CATEGORIES = [
  { id: "chama-fruit", label: "Frutas y Verduras" },
  { id: "chama-max", label: "Construccion" },
  { id: "chama-todo", label: "Otros Alimentos" },
];

const STATUS_META = {
  draft: { label: "Borrador", tone: "bg-gray-100 text-gray-700 border-gray-200" },
  sent: { label: "Enviada", tone: "bg-amber-100 text-amber-700 border-amber-200" },
  fulfilled: { label: "Completada", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PLACEHOLDER_IMAGE = "https://placehold.co/96";

const formatQuantity = (value) => {
  const amount = toNumber(value);
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString("de-DE", hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
    : undefined);
};

const resolveItemProductName = (item) =>
  item?.Variety?.Product?.productName || item?.productName || "";

const resolveItemVarietyName = (item) =>
  item?.varietyName || item?.Variety?.varietyName || "Producto";

const resolveItemImage = (item) =>
  item?.varietyImage ||
  item?.Variety?.varietyImage ||
  item?.Variety?.Product?.productImage ||
  PLACEHOLDER_IMAGE;

const buildItemDisplayName = (item) => {
  const productName = String(resolveItemProductName(item) || "").trim();
  const varietyName = String(resolveItemVarietyName(item) || "").trim();
  if (productName && varietyName && productName.toLowerCase() !== varietyName.toLowerCase()) {
    return `${productName} - ${varietyName}`;
  }
  return productName || varietyName || "Producto";
};

const normalizeMetricUnit = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const StockRequestDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branchId: routeBranchId, id } = useParams();
  const branchId = routeBranchId || localStorage.getItem("selectedBranchId");
  const { user } = useCurrentUser();

  const canReadStock = hasPermission(user, "stock.read");
  const canCreateStock = hasPermission(user, "stock.create");
  const canTransferGuide = hasPermission(user, "delivery_guide.transfer");

  const [requestList, setRequestList] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lastStockItems, setLastStockItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const [editPayload, setEditPayload] = useState({ name: "", notes: "" });
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [addPayload, setAddPayload] = useState({ quantity: "", metricUnit: "" });

  const displayCategories = categories.length ? categories : STATIC_CATEGORIES;
  const addParam = searchParams.get("add");

  const resolveProductCategoryId = useCallback(
    (product) => {
      return product?.categoryId ?? product?.Category?.id ?? null;
    },
    []
  );

  const resolveCategoryLabel = useCallback(
    (category) => category?.name || category?.label || String(category?.id ?? ""),
    []
  );

  const isEditable = useMemo(() => {
    if (!requestList) return false;
    return (
      canCreateStock &&
      String(requestList.branchId) === String(branchId) &&
      requestList.status === "draft"
    );
  }, [requestList, canCreateStock, branchId]);

  const varietyRequestCounts = useMemo(() => {
    const counts = {};
    (requestList?.Items || []).forEach((item) => {
      const varietyId = Number(item?.varietyId);
      if (!Number.isInteger(varietyId)) return;
      counts[varietyId] = (counts[varietyId] || 0) + 1;
    });
    return counts;
  }, [requestList]);

  const metricOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    (selectedVariety?.VarietyPrices || []).forEach((price) => {
      const normalized = normalizeMetricUnit(price?.metricUnit);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      options.push(normalized);
    });
    return options;
  }, [selectedVariety]);

  const loadData = useCallback(async () => {
    if (!branchId || !id || !canReadStock) return;
    setLoading(true);
    setError("");
    try {
      const [listData, productsData, categoriesData, stockData] = await Promise.all([
        getStockRequestById(branchId, id),
        getProducts(branchId, { pageSize: 0 }).catch(() => ({ data: [] })),
        getCategories({ branchId }).catch(() => []),
        getLastStockItemForEachVariety(branchId, { pageSize: 0 }).catch(() => ({ data: [] })),
      ]);

      const normalizedProducts = normalizePaginatedResponse(productsData);
      const normalizedStock = normalizePaginatedResponse(stockData);
      const stockItemsMap = (normalizedStock.data || []).reduce((acc, item) => {
        if (Number.isInteger(item?.varietyId)) {
          acc[item.varietyId] = item;
        }
        return acc;
      }, {});

      setRequestList(listData);
      setEditPayload({
        name: listData?.name || "",
        notes: listData?.notes || "",
      });
      setProducts(normalizedProducts.data || []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setLastStockItems(stockItemsMap);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo cargar la solicitud.");
    } finally {
      setLoading(false);
    }
  }, [branchId, id, canReadStock]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!requestList || addParam !== "1") return;
    if (isEditable) {
      setIsPickerOpen(true);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("add");
    setSearchParams(nextParams, { replace: true });
  }, [requestList, addParam, isEditable, searchParams, setSearchParams]);

  const handleSaveHeader = async () => {
    if (!isEditable) return;
    setActionBusy(true);
    setActionError("");
    try {
      const response = await updateStockRequest(branchId, id, {
        name: editPayload.name,
        notes: editPayload.notes,
      });
      if (response?.requestList) {
        setRequestList(response.requestList);
      } else {
        await loadData();
      }
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo guardar la solicitud.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!isEditable) return;
    const approved = window.confirm("Eliminar esta solicitud?");
    if (!approved) return;
    setActionBusy(true);
    setActionError("");
    try {
      await deleteStockRequest(branchId, id);
      navigate(`/branches/${branchId}/stock-requests`);
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo eliminar la solicitud.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleSendRequest = async () => {
    if (!isEditable) return;
    const approved = window.confirm("Enviar solicitud a la sucursal principal?");
    if (!approved) return;
    setActionBusy(true);
    setActionError("");
    try {
      const response = await sendStockRequest(branchId, id);
      if (response?.requestList) {
        setRequestList(response.requestList);
      } else {
        await loadData();
      }
      setIsPickerOpen(false);
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo enviar la solicitud.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleSelectVariety = (variety, product) => {
    setSelectedVariety({
      ...variety,
      productName: product?.productName || variety?.productName || "",
    });
    setAddPayload({ quantity: "", metricUnit: "" });
    setActionError("");
  };

  const handleCloseAddModal = () => {
    setSelectedVariety(null);
    setAddPayload({ quantity: "", metricUnit: "" });
  };

  const handleAddItem = async () => {
    if (!isEditable || !selectedVariety) return;
    const quantity = Number(String(addPayload.quantity).replace(",", "."));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setActionError("Ingresa una cantidad valida.");
      return;
    }
    setActionBusy(true);
    setActionError("");
    try {
      const response = await addStockRequestItem(branchId, id, {
        varietyId: Number(selectedVariety.id),
        metricUnit: addPayload.metricUnit || undefined,
        requestedQuantity: quantity,
      });
      if (response?.requestList) {
        setRequestList(response.requestList);
      } else {
        await loadData();
      }
      setAddPayload({ quantity: "", metricUnit: "" });
      setSelectedVariety(null);
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo agregar el item.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleUpdateItemQuantity = async (item, nextQuantity) => {
    if (!isEditable) return;
    const parsed = Number(String(nextQuantity).replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setActionError("Cantidad invalida.");
      return;
    }
    setActionBusy(true);
    setActionError("");
    try {
      const response = await updateStockRequestItem(branchId, id, item.id, {
        requestedQuantity: parsed,
      });
      if (response?.requestList) {
        setRequestList(response.requestList);
      } else {
        await loadData();
      }
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo actualizar el item.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!isEditable) return;
    const approved = window.confirm(`Eliminar ${item.varietyName || "item"}?`);
    if (!approved) return;
    setActionBusy(true);
    setActionError("");
    try {
      const response = await deleteStockRequestItem(branchId, id, item.id);
      if (response?.requestList) {
        setRequestList(response.requestList);
      } else {
        await loadData();
      }
    } catch (err) {
      setActionError(err?.response?.data?.message || "No se pudo eliminar el item.");
    } finally {
      setActionBusy(false);
    }
  };

  if (!canReadStock) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border p-4 text-sm text-gray-600">
          No tienes permisos para ver solicitudes.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <InlineLoading label="Cargando solicitud" />
      </div>
    );
  }

  if (error || !requestList) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border p-4 text-sm text-red-600">
          {error || "Solicitud no encontrada."}
        </div>
      </div>
    );
  }

  const statusMeta = STATUS_META[requestList.status] || STATUS_META.draft;
  const requestItems = Array.isArray(requestList.Items) ? requestList.Items : [];
  const completionRatio = Math.max(0, Math.min(1, Number(requestList.progress?.completionRatio || 0)));
  const selectedLastPurchase = selectedVariety
    ? lastStockItems[selectedVariety.id]
    : null;
  const isMainBranchReview = String(requestList.targetBranchId) === String(branchId);
  const canCreateTransferForRequest =
    canTransferGuide &&
    isMainBranchReview &&
    requestList.status !== "draft" &&
    toNumber(requestList.progress?.remainingTotal) > 0;

  const handleGoToCreateTransfer = () => {
    if (!canCreateTransferForRequest) return;
    navigate(`/branches/${branchId}/delivery-guides`, {
      state: {
        prefillTransfer: {
          destinationBranchId: requestList.branchId,
          requestListId: requestList.id,
          requestListName: requestList.name || `Solicitud #${requestList.id}`,
        },
      },
    });
  };

  const renderQuantityEditor = (item, className = "") => (
    <input
      type="text"
      defaultValue={toNumber(item.requestedQuantity)}
      onBlur={(event) => {
        const value = Number(String(event.target.value).replace(",", "."));
        if (
          !Number.isFinite(value) ||
          value <= 0 ||
          Math.abs(value - toNumber(item.requestedQuantity)) < 1e-6
        ) {
          return;
        }
        handleUpdateItemQuantity(item, value);
      }}
      className={`border border-gray-300 rounded px-2 py-1 text-right text-sm ${className}`.trim()}
      disabled={actionBusy}
    />
  );

  const renderItemCard = (item) => {
    const pendingQuantity = toNumber(item.remainingQuantity);
    return (
      <article key={item.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex gap-3">
          <img
            src={resolveItemImage(item)}
            alt={buildItemDisplayName(item)}
            className="h-16 w-16 shrink-0 rounded object-cover"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-snug text-gray-900">
                  {buildItemDisplayName(item)}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">Unidad: {item.metricUnit || "-"}</p>
              </div>
              {isEditable && (
                <button
                  type="button"
                  onClick={() => handleDeleteItem(item)}
                  disabled={actionBusy}
                  className="inline-flex shrink-0 items-center justify-center rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-60"
                  aria-label={`Eliminar ${buildItemDisplayName(item)}`}
                  title="Eliminar"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div className="rounded bg-gray-50 px-2 py-1.5">
                <p className="text-[11px] uppercase text-gray-500">Solicitado</p>
                {isEditable ? (
                  renderQuantityEditor(item, "mt-1 w-full")
                ) : (
                  <p className="font-semibold text-gray-900">{formatQuantity(item.requestedQuantity)}</p>
                )}
              </div>
              <div className="rounded bg-gray-50 px-2 py-1.5">
                <p className="text-[11px] uppercase text-gray-500">Completado</p>
                <p className="font-semibold text-gray-900">{formatQuantity(item.fulfilledQuantity)}</p>
              </div>
              <div className={`rounded px-2 py-1.5 ${pendingQuantity > 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
                <p className={`text-[11px] uppercase ${pendingQuantity > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  Pendiente
                </p>
                <p className={`font-semibold ${pendingQuantity > 0 ? "text-amber-900" : "text-emerald-900"}`}>
                  {formatQuantity(item.remainingQuantity)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate(`/branches/${branchId}/stock-requests`)}
            className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            <FiArrowLeft />
            Volver a solicitudes
          </button>
          <span className={`inline-flex w-fit rounded-full border px-2 py-1 text-xs ${statusMeta.tone}`}>
            {statusMeta.label}
          </span>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase text-gray-500">
                {isMainBranchReview ? "Solicitud entrante" : "Solicitud a matriz"}
              </p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900">
                {requestList.name || `Solicitud #${requestList.id}`}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {requestList.RequestBranch?.branchName || "-"} hacia {requestList.TargetBranch?.branchName || "-"}
              </p>
            </div>
            {!isEditable && canCreateTransferForRequest && (
              <button
                type="button"
                onClick={handleGoToCreateTransfer}
                className="inline-flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 sm:w-fit"
              >
                <FiTruck />
                Crear guía de traslado
              </button>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
            <div className="rounded bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Items</p>
              <p className="font-semibold text-gray-900">{requestItems.length}</p>
            </div>
            <div className="rounded bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Solicitado</p>
              <p className="font-semibold text-gray-900">{formatQuantity(requestList.progress?.requestedTotal)}</p>
            </div>
            <div className="rounded bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Completado</p>
              <p className="font-semibold text-gray-900">{formatQuantity(requestList.progress?.fulfilledTotal)}</p>
            </div>
            <div className="rounded bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Pendiente</p>
              <p className="font-semibold text-gray-900">{formatQuantity(requestList.progress?.remainingTotal)}</p>
            </div>
            <div className="rounded bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Enviada</p>
              <p className="font-semibold text-gray-900">{formatDateTime(requestList.sentAt)}</p>
            </div>
          </div>

          <div className="mt-3">
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${completionRatio * 100}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Completado {Math.round(completionRatio * 100)}%</p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                value={editPayload.name}
                onChange={(event) =>
                  setEditPayload((prev) => ({ ...prev, name: event.target.value }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                disabled={!isEditable || actionBusy}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <input
                type="text"
                value={editPayload.notes}
                onChange={(event) =>
                  setEditPayload((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                disabled={!isEditable || actionBusy}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
            <p>
              Creada:{" "}
              <span className="font-medium text-gray-800">
                {formatDateTime(requestList.createdAt)}
              </span>
            </p>
            <p>
              Completada:{" "}
              <span className="font-medium text-gray-800">
                {formatDateTime(requestList.fulfilledAt)}
              </span>
            </p>
          </div>

          {isEditable && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSaveHeader}
                disabled={actionBusy}
                className="inline-flex items-center gap-2 rounded bg-slate-700 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                <FiSave />
                Guardar
              </button>
              <button
                type="button"
                onClick={handleSendRequest}
                disabled={actionBusy}
                className="inline-flex items-center gap-2 rounded bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              >
                <FiSend />
                Enviar solicitud
              </button>
              <button
                type="button"
                onClick={() => setIsPickerOpen((prev) => !prev)}
                disabled={actionBusy}
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                <FiPlus />
                {isPickerOpen ? "Ocultar productos" : "Agregar productos"}
              </button>
              <button
                type="button"
                onClick={handleDeleteRequest}
                disabled={actionBusy}
                className="inline-flex items-center gap-2 rounded bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                <FiTrash2 />
                Eliminar
              </button>
            </div>
          )}

          {!isEditable && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-gray-500">
                Esta solicitud esta en modo solo lectura para esta sucursal o estado.
              </p>
            </div>
          )}

          {actionError && <p className="text-sm text-red-600">{actionError}</p>}
        </div>

        {isEditable && isPickerOpen && (
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <StockProductPickerPanel
              products={products}
              categories={displayCategories}
              resolveProductCategoryId={resolveProductCategoryId}
              resolveCategoryLabel={resolveCategoryLabel}
              onSelectVariety={handleSelectVariety}
              getVarietyBadge={(variety) => {
                const count = Number(varietyRequestCounts[variety.id] || 0);
                const isAdded = count > 0;
                return {
                  label: isAdded ? `Agregado${count > 1 ? ` x${count}` : ""}` : "Faltante",
                  cardClassName: isAdded
                    ? "bg-emerald-50/70 border-emerald-300 ring-1 ring-emerald-200"
                    : "bg-amber-50/70 border-amber-300",
                  badgeClassName: isAdded
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700",
                };
              }}
              renderVarietyReference={(variety, context) => {
                const wholesalePrices = (context?.sellablePrices || []).filter(
                  (price) =>
                    resolvePriceClientType(price) === "mayorista" &&
                    Number.isFinite(Number(price.price))
                );
                const wholesaleReference =
                  wholesalePrices.length > 0
                    ? [...wholesalePrices].sort(
                        (entryA, entryB) => Number(entryA.price) - Number(entryB.price)
                      )[0]
                    : null;
                const lastPurchase = lastStockItems[variety.id] || null;
                return (
                  <>
                    {context?.isVarietySoldOut ? (
                      <p className="text-red-600 text-xs font-semibold mt-1">AGOTADO</p>
                    ) : wholesaleReference ? (
                      <p className="text-slate-600 text-xs mt-1">
                        Mayorista ref: $
                        {Number(wholesaleReference.price || 0).toLocaleString("de-DE")}{" "}
                        {wholesaleReference.metricUnit || ""}
                      </p>
                    ) : (
                      <p className="text-slate-500 text-xs mt-1">Sin precio mayorista</p>
                    )}
                    <p className="text-slate-500 text-xs mt-1">
                      Ultima compra:{" "}
                      {lastPurchase
                        ? `$${Number(lastPurchase.price || 0).toLocaleString("de-DE")}`
                        : "Sin registro"}
                    </p>
                  </>
                );
              }}
            />
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Productos solicitados</h2>
              <p className="text-sm text-gray-600">
                {requestItems.length} {requestItems.length === 1 ? "producto" : "productos"} en la solicitud.
              </p>
            </div>
            <FiPackage className="shrink-0 text-gray-400" size={22} />
          </div>

          {requestItems.length ? (
            <>
              <div className="space-y-3 md:hidden">
                {requestItems.map(renderItemCard)}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Producto</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Unidad</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Solicitado</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Completado</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Pendiente</th>
                      {isEditable && <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {requestItems.map((item) => {
                      const pendingQuantity = toNumber(item.remainingQuantity);
                      return (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-3">
                              <img
                                src={resolveItemImage(item)}
                                alt={buildItemDisplayName(item)}
                                className="h-12 w-12 shrink-0 rounded object-cover"
                                loading="lazy"
                              />
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900">{buildItemDisplayName(item)}</p>
                                {resolveItemProductName(item) && (
                                  <p className="text-xs text-gray-500">{resolveItemVarietyName(item)}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">{item.metricUnit || "-"}</td>
                          <td className="px-3 py-2 text-right">
                            {isEditable
                              ? renderQuantityEditor(item, "w-24")
                              : formatQuantity(item.requestedQuantity)}
                          </td>
                          <td className="px-3 py-2 text-right">{formatQuantity(item.fulfilledQuantity)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${pendingQuantity > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                            {formatQuantity(item.remainingQuantity)}
                          </td>
                          {isEditable && (
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteItem(item)}
                                disabled={actionBusy}
                                className="inline-flex items-center justify-center rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-60"
                                aria-label={`Eliminar ${buildItemDisplayName(item)}`}
                                title="Eliminar"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
              No hay items en esta solicitud.
            </div>
          )}
        </div>
      </div>

      {selectedVariety && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex min-w-0 items-start gap-3">
                <img
                  src={selectedVariety.varietyImage || PLACEHOLDER_IMAGE}
                  alt={selectedVariety.varietyName || "Producto"}
                  className="h-14 w-14 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Agregar a solicitud
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {selectedVariety.productName || "Producto"}
                  </h2>
                  <p className="text-sm text-slate-600">{selectedVariety.varietyName}</p>
                </div>
              </div>
              <button
                onClick={handleCloseAddModal}
                className="rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                <FiX />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {selectedLastPurchase && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                  <p className="text-sm text-slate-700">
                    Ultima compra ({new Date(selectedLastPurchase.createdAt).toLocaleDateString()}) - Precio: $
                    {Number(selectedLastPurchase.price || 0).toLocaleString("de-DE")}, Cantidad:{" "}
                    {Number(selectedLastPurchase.quantity || 0).toLocaleString("de-DE")}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Cantidad solicitada</label>
                <input
                  type="text"
                  value={addPayload.quantity}
                  onChange={(event) =>
                    setAddPayload((prev) => ({ ...prev, quantity: event.target.value }))
                  }
                  className="border border-gray-300 rounded p-2 w-full"
                  placeholder="Ej: 12 o 12.5"
                  disabled={actionBusy}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Unidad (opcional)</label>
                <select
                  value={addPayload.metricUnit}
                  onChange={(event) =>
                    setAddPayload((prev) => ({ ...prev, metricUnit: event.target.value }))
                  }
                  className="border border-gray-300 rounded p-2 w-full"
                  disabled={actionBusy}
                >
                  <option value="">Sin especificar</option>
                  {metricOptions.map((metricUnit) => (
                    <option key={metricUnit} value={metricUnit}>
                      {metricUnit}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={handleCloseAddModal}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
                disabled={actionBusy}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddItem}
                disabled={actionBusy}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                type="button"
              >
                {actionBusy ? <InlineLoading label="Agregando" /> : "Agregar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockRequestDetailPage;
